import styles from '../provide-transactions/PSD2Connection.module.css';
import Text from '../../../../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../../../../enums';
import screeningSteps, { Action } from '../../../../../organisms/screening-navigation/screeningSteps';
import { Card } from '../../../../../organisms/card';
import { Trans, useTranslation } from 'react-i18next';
import { Tooltip } from '../../../../../atoms/tooltip';
import QuestionLineIcon from '../../../../../atoms/icons/QuestionLineIcon';
import { SepaCountrySelector } from '../../../../../organisms/sepa-country-selector';
import React, { useState } from 'react';
import { ListOfBanksSelector } from '../../../../../organisms/list-of-banks-selector';
import ScreeningNavigation from '../../../../../organisms/screening-navigation/ScreeningNavigation';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRoutePath } from '../../../../../utils/useRoutePath';
import FormError from '../../../../../molecules/form/FormError';
import Form, { Data, FormErrors } from '../../../../../molecules/form/Form';
import Label from '../../../../../atoms/label/Label';
import generateAgreement from '../../../../../../api/screening/income-verification/generate-agreement/generateAgreement';
import i18n from 'i18next';
import Notifications from '../../../../../organisms/notifications/Notifications';
import PageContentNotFound from '../../../../../organisms/page-content-not-found/PageContentNotFound';

const PSD2Connection = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const routePath = useRoutePath();
    const { homeseekerId } = useParams();
    const [ searchParams ] = useSearchParams();

    const authorisationId = searchParams.get('authorisationId');

    const [ , setFormData ] = useState<Data>({});
    const [ , setFormValid ] = useState<boolean>(false);
    const [ formErrors, setFormErrors ] = useState<FormErrors>({});

    const [selectedSepaCountryCode, setSelectedSepaCountryCode ] = useState('');
    const [selectedBankId, setSelectedBankId] = useState<string | null>(null);

    const  handleBankChange = (value) => {
        setSelectedBankId(value);
    }

    const handleSubmit = async () => {
        try {
            const currentLanguage =  i18n.language || 'nl';
            const generateAgreementResponse = await generateAgreement(
                selectedBankId,
                homeseekerId,
                currentLanguage
            );

            // TODO: add response type that includes requisitionId and authorisationLink
            localStorage.setItem('goCardlessAgreementId', generateAgreementResponse.requisitionId)
            window.location.href = generateAgreementResponse.authorisationLink;
        } catch (error) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        }
    }

    const handleFormChange = (data, isValid, errors) => {
        setFormData(data);
        setFormValid(isValid);
        setFormErrors(errors);
    }

    const handleClick = (action: Action) => {
        if (action === 'previous') {
            navigate(routePath('ScreeningIncomeVerificationProvideTransactions', { homeseekerId }, { authorisationId }));
        }
        if (action === 'skip') {
            navigate(routePath('ScreeningAdditionalInformation', { homeseekerId }, { authorisationId }));
        }
    }

    if (!authorisationId) {
        return <PageContentNotFound/>;
    }

    return (
        <Form
            onSubmit={handleSubmit}
            onChange={handleFormChange}
        >
            <div className={styles.contentContainer}>
                <div className={styles.gridLg}>
                    <Text
                        color={ColorEnum.Primary700}
                        weight={FontWeightEnum.Bold}
                        size={FontSizeEnum.DisplayXs}
                    >{t(screeningSteps.ScreeningIncomeVerificationProvideTransactions.pageTitleTranslationKey)}</Text>
                    <Card elevated>
                        <div className={styles.gridMd}>
                            <Text
                                tag='p'
                                color={ColorEnum.Gray800}
                                weight={FontWeightEnum.Semibold}>
                                {t('screening.incomeVerification.provideTransactions.psd2Connection.label')}
                            </Text>
                            <Text>
                                <Trans
                                    i18nKey="screening.incomeVerification.provideTransactions.psd2Connection.supportingText"
                                    components={{
                                        strong: <strong/>,
                                        tooltip:
                                            <Tooltip
                                                className={styles.tooltip}
                                                size='md'
                                                anchor={
                                                    <div className={styles.tooltipAnchor}>
                                                        <QuestionLineIcon/>
                                                    </div>
                                                }
                                            >{}</Tooltip>
                                    }}
                                />
                            </Text>
                            <SepaCountrySelector
                                value={selectedSepaCountryCode}
                                onChange={setSelectedSepaCountryCode}
                            />
                            <FormError errors={formErrors.sepaCountrySelector} />
                            {
                                selectedSepaCountryCode && (
                                    <div>
                                        <Label
                                            htmlFor='listOfBanksSelector'
                                        >
                                            {t('screening.incomeVerification.provideTransactions.listOfBanksSelector.label')}
                                        </Label>
                                        <ListOfBanksSelector
                                            countryCode={selectedSepaCountryCode}
                                            name='listOfBanksSelector'
                                            required={true}
                                            onChange={handleBankChange}
                                        />
                                        <FormError errors={formErrors.listOfBanksSelector} />
                                    </div>
                                )
                            }
                        </div>
                    </Card>
                    <ScreeningNavigation
                        onClick={handleClick}
                    />
                </div>
            </div>
        </Form>
    );
}

export default PSD2Connection;
